/**
 * 开发环境自动登录服务
 *
 * 职责：
 * - 判断当前环境是否允许执行开发环境自动登录
 * - 校验凭据配置
 * - 调用真实后端 UserService.login
 * - 返回结构化结果，供 authStore 做会话落地
 *
 * 约束：
 * - 不直接写 Pinia 状态
 * - 不触发路由跳转
 * - 使用依赖注入便于单元测试
 */

import { authClient } from '@/services/auth/client';
import { apiConfig } from '@/services/api/config';
import type { LoginResponse } from './contracts';

export interface DevAuthConfig {
  autoLoginEnabled: boolean;
  autoLoginUsername: string;
  autoLoginPassword: string;
  autoLoginTenantId: string;
}

export type AuthLoginFn = (req: {
  username: string;
  password: string;
  tenantId: bigint;
}) => Promise<LoginResponse>;

export type AttemptLoginResult =
  | { status: 'skipped'; reason: 'not-dev-env' | 'disabled' | 'credentials-missing' }
  | { status: 'success'; response: LoginResponse }
  | { status: 'failed'; error: unknown };

export interface DevelopmentAutoLoginDeps {
  config: DevAuthConfig;
  isDev: boolean;
  login: AuthLoginFn;
}

export function createDevelopmentAutoLoginService(deps: DevelopmentAutoLoginDeps) {
  return {
    async attemptLogin(): Promise<AttemptLoginResult> {
      if (!deps.isDev) {
        return { status: 'skipped', reason: 'not-dev-env' };
      }
      if (!deps.config.autoLoginEnabled) {
        return { status: 'skipped', reason: 'disabled' };
      }
      if (!deps.config.autoLoginUsername || !deps.config.autoLoginPassword) {
        console.warn('[dev-auto-login] 用户名或密码缺失，跳过自动登录。请检查 packages/web-client/.env.development.local');
        return { status: 'skipped', reason: 'credentials-missing' };
      }

      try {
        const tenantId = deps.config.autoLoginTenantId
          ? BigInt(deps.config.autoLoginTenantId)
          : 0n;
        const response = await deps.login({
          username: deps.config.autoLoginUsername,
          password: deps.config.autoLoginPassword,
          tenantId,
        });
        return { status: 'success', response };
      } catch (error) {
        console.warn('[dev-auto-login] 自动登录失败', error);
        return { status: 'failed', error };
      }
    },
  };
}

/** 生产路径使用的默认实例 —— 由 authStore 直接消费 */
export const developmentAutoLoginService = createDevelopmentAutoLoginService({
  config: apiConfig.devAuth,
  isDev: import.meta.env.DEV,
  login: (req) => authClient.login(req),
});
